import { CapacityTagDto } from 'Models/Shared/Capacity/CapacityTagDto';

/** What a class's remaining space means for the party trying to book it. */
export type FareAvailability = 'AVAILABLE' | 'LOW' | 'NOT_ENOUGH' | 'SOLD_OUT';

/**
 * Below this share of a class's allowance, a fare is drawn as running out.
 *
 * <p>A share rather than a number, because the number means different things on different vessels:
 * five left is a warning on a class of twenty and nothing at all on a class of four hundred.</p>
 */
export const LOW_AVAILABILITY_SHARE = 0.2;

/**
 * How a class is doing on one sailing, for the booker in front of it.
 *
 * <p>Four states, because to a booker they are four different facts. <b>Sold out</b> is gone for
 * everyone. <b>Not enough</b> is gone for <i>them</i> — a family of four looking at three seats —
 * and is the one most easily mistaken for availability, because the class does have room.
 * <b>Low</b> is bookable but worth hurrying. Everything else is simply available.</p>
 *
 * <p>Without a total there is no share to compute, so a class the calculator does not size reports
 * available rather than inventing a threshold.</p>
 */
export const fareAvailability = (tag: CapacityTagDto | undefined, partySize: number): FareAvailability => {
	if (!tag) {
		return 'AVAILABLE';
	}
	if (tag.remaining <= 0) {
		return 'SOLD_OUT';
	}
	if (tag.remaining < Math.max(partySize, 1)) {
		return 'NOT_ENOUGH';
	}
	if (tag.total && tag.total > 0 && tag.remaining / tag.total < LOW_AVAILABILITY_SHARE) {
		return 'LOW';
	}
	return 'AVAILABLE';
};

/** The class capacity tag for one class on a trip, or undefined where the trip carries none. */
export const classCapacityTag = (
	tags: CapacityTagDto[] | undefined,
	passengerClassId: string,
): CapacityTagDto | undefined => (tags ?? []).find(x => x.category === 'PassengerClass' && x.id === passengerClassId);

/**
 * The seats-left line under a fare, or null where there is no number to print.
 *
 * <p>Null in two different situations that look the same from here. A class configured
 * <c>ShowCapacity: false</c> is deliberately silent about its numbers — the operator wants
 * available-or-not and nothing more — and a sold-out cell has already said so in its price slot,
 * where "Sold out" replaces the figure. Neither wants a second line.</p>
 */
export const capacityLabel = (tag: CapacityTagDto | undefined, availability: FareAvailability): string | null => {
	if (!tag || tag.displayMode !== 'ShowValue' || availability === 'SOLD_OUT') {
		return null;
	}

	const seats = tag.remaining === 1 ? '1 seat' : `${tag.remaining} seats`;

	// "Only" is the whole difference between a fact and a prompt, so it is spent on the two states
	// that are running out and withheld from the one that is not.
	return availability === 'AVAILABLE' ? `${seats} left` : `Only ${seats} left`;
};

/**
 * Whether a sailing has no class a booker could take.
 *
 * <p>Asked of the row rather than the cells because the answer changes what the row is: a sailing
 * with one class left is a choice with fewer options, and a sailing with none is not a choice at
 * all — it stops offering fares and offers the waitlist instead.</p>
 *
 * <p>A party of four looking at three seats counts as gone. The class is not sold out, but it is
 * sold out <i>to them</i>, and a row that cannot be booked should not be drawn as though it can.</p>
 */
export const sailingIsGone = (
	tags: CapacityTagDto[] | undefined,
	passengerClassIds: string[],
	partySize: number,
): boolean =>
	passengerClassIds.length > 0 &&
	passengerClassIds.every(id => {
		const availability = fareAvailability(classCapacityTag(tags, id), partySize);
		return availability === 'SOLD_OUT' || availability === 'NOT_ENOUGH';
	});

/** A fare type the booking puts more passengers into than it has places left. */
export interface OverfullFareType {
	passengerClassId: string;
	/** How many of this booking's passengers are in it on this sailing. */
	count: number;
	remaining: number;
}

/**
 * The fare types on a sailing that this booking's own passengers overfill.
 *
 * <p>Per fare type, not per sailing. A sailing with forty places left can still have one left in
 * Premium, and a party of three all in Premium does not fit it — the whole-sailing count says they
 * do. A passenger with no fare type counts against none, and a fare type the trip sends no capacity
 * figure for is never overfull: no number is not the same as no room.</p>
 */
export const overfullFareTypes = (
	tags: CapacityTagDto[] | undefined,
	passengerClassIds: (string | undefined)[],
): OverfullFareType[] => {
	const counts = new Map<string, number>();
	passengerClassIds.forEach(id => {
		if (id) {
			counts.set(id, (counts.get(id) ?? 0) + 1);
		}
	});

	const out: OverfullFareType[] = [];
	counts.forEach((count, passengerClassId) => {
		const tag = classCapacityTag(tags, passengerClassId);
		if (tag && count > tag.remaining) {
			out.push({ passengerClassId, count, remaining: tag.remaining });
		}
	});
	return out;
};
